import api from "../../api/client";
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Space,
  Spin,
  Typography,
  Tag,
  Tooltip,
  Button,
  message,
  Divider,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Link } from "react-router-dom";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;
const { TextArea } = Input;

const EggCollectionsDashboard = () => {
  const account = useSelector((state) => state.account);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [batchOptions, setBatchOptions] = useState([]);
  const [farmOptions, setFarmOptions] = useState([]);
  const [filters, setFilters] = useState({
    batch: null,
    farm: null,
    dateRange: null,
    urlToUse: "current",
  });

  // Edit / Void state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm] = Form.useForm();

  const urlOptions = [
    { value: "current", label: "Current Inventory" },
    { value: "history", label: "Collections History" },
  ];

  // ---------------- Fetch batches ----------------
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const response = await api.get(`/api/animals/poultry/layers`);
        const batches = (response.data.data || []).map((item) => ({
          value: item.batchId,
          label: `${item.batchId} (${item.farm_name})`,
        }));
        setBatchOptions(batches);
      } catch (error) {
        console.error("Error fetching batches:", error);
        message.error("Failed to load batches");
      }
    };

    const fetchFarms = async () => {
      try {
        const response = await api.get(`/farm`);
        const farms = (response.data || []).map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setFarmOptions(farms);
      } catch (error) {
        console.error("Error fetching farms:", error);
        message.error("Failed to load farms");
      }
    };
    fetchBatches();
    fetchFarms();
  }, []);

  const url =
    filters.urlToUse === "history"
      ? `/eggs/collections-history`
      : `/eggs/collections`;

  // ---------------- Fetch collections ----------------
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.batch) params.batch = filters.batch;
      if (filters.farm) params.farm = filters.farm;
      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].format("YYYY-MM-DD");
        params.endDate = filters.dateRange[1].format("YYYY-MM-DD");
      }
      const response = await api.get(url, { params });
      setData(response.data);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to load collections";
      console.error("Error fetching collections:", msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [filters]);

  // ---------------- Edit handler ----------------
  const openEditModal = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      batch: record.batch,
      recorded: record.recorded ? dayjs(record.recorded) : null,
      remarks: record.remarks,
      normal_trays: record.normal_trays,
      normal_eggs: record.normal_eggs,
      broken_trays: record.broken_trays,
      broken_eggs: record.broken_eggs,
      double_yolk_trays: record.double_yolk_trays,
      double_yolk_eggs: record.double_yolk_eggs,
      medium_trays: record.medium_trays,
      medium_eggs: record.medium_eggs,
      spoilt_trays: record.spoilt_trays,
      spoilt_eggs: record.spoilt_eggs,
    });
    setEditModalVisible(true);
  };

  const handleSaveEdit = async (values) => {
    if (!editingRecord) return;
    setSavingEdit(true);
    try {
      const payload = {
        ...values,
        recorded: values.recorded
          ? dayjs(values.recorded).format("YYYY-MM-DD")
          : editingRecord.recorded,
        // keep original batch/farm/user info
        batch: editingRecord.batch,
        farm: editingRecord.farm,
        userId: account?.user?.id || editingRecord.userId,
        userName: account?.user?.firstname || editingRecord.userName,
        // carry forward total columns computed
        collected: values.collected ?? editingRecord.collected,
        spoilt: values.spoilt ?? editingRecord.spoilt,
        numOfTrays: values.numOfTrays ?? editingRecord.numOfTrays,
        remainingEggs: values.remainingEggs ?? editingRecord.remainingEggs,
        double_yolk: values.double_yolk ?? editingRecord.double_yolk,
        middle_sized: values.middle_sized ?? editingRecord.middle_sized,
        badEggs: values.badEggs ?? editingRecord.badEggs,
      };

      const res = await api.put(
        `/eggs/collections/${editingRecord.id}`,
        payload,
      );
      if (res.data.success) {
        message.success("Collection updated successfully");
        setEditModalVisible(false);
        setEditingRecord(null);
        editForm.resetFields();
        fetchCollections();
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update collection";
      console.error("Update error:", msg);
      message.error(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------------- Void handler ----------------
  const handleVoid = async (record) => {
    try {
      const res = await api.delete(`/eggs/collections/${record.id}`, {
        data: {
          userId: account?.user?.id,
          userName: account?.user?.firstname,
          reason: "Voided from dashboard",
        },
      });
      if (res.data.success) {
        message.success("Collection voided successfully");
        fetchCollections();
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to void collection";
      console.error("Void error:", msg);
      message.error(msg);
    }
  };

  // ---------------- Totals ----------------
  const totals = data.reduce(
    (acc, item) => {
      acc.normal.trays += item.normal_trays || 0;
      acc.normal.eggs += item.normal_eggs || 0;
      acc.broken.trays += item.broken_trays || 0;
      acc.broken.eggs += item.broken_eggs || 0;
      acc.doubleYolk.trays += item.double_yolk_trays || 0;
      acc.doubleYolk.eggs += item.double_yolk_eggs || 0;
      acc.medium.trays += item.medium_trays || 0;
      acc.medium.eggs += item.medium_eggs || 0;
      acc.spoilt.trays += item.spoilt_trays || 0;
      acc.spoilt.eggs += item.spoilt_eggs || 0;
      return acc;
    },
    {
      normal: { trays: 0, eggs: 0 },
      broken: { trays: 0, eggs: 0 },
      doubleYolk: { trays: 0, eggs: 0 },
      medium: { trays: 0, eggs: 0 },
      spoilt: { trays: 0, eggs: 0 },
    },
  );

  // ---------------- Columns ----------------
  const columns = [
    {
      title: "Date",
      dataIndex: "recorded",
      key: "recorded",
      render: (text) => moment(text).format("YYYY-MM-DD"),
      sorter: (a, b) => moment(a.recorded).unix() - moment(b.recorded).unix(),
      defaultSortOrder: "descend",
    },
    {
      title: "Batch",
      dataIndex: "batch",
      key: "batch",
      filters: batchOptions.map((opt) => ({
        text: opt.label,
        value: opt.value,
      })),
      onFilter: (value, record) => record.batch === value,
    },
    {
      title: "Normal",
      dataIndex: "normal_trays",
      key: "normal_trays",
      sorter: (a, b) => a.normal_trays - b.normal_trays,
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "N. Remainder",
      dataIndex: "normal_eggs",
      key: "normal_eggs",
      sorter: (a, b) => a.normal_eggs - b.normal_eggs,
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Broken",
      dataIndex: "broken_trays",
      key: "broken_trays",
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Double Yolk",
      dataIndex: "double_yolk_trays",
      key: "double_yolk_trays",
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Medium",
      dataIndex: "medium_trays",
      key: "medium_trays",
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Spoilt",
      dataIndex: "spoilt_trays",
      key: "spoilt_trays",
      render: (val) => val?.toLocaleString(),
    },
    {
      title: "Laying Period",
      key: "layingPeriod",
      render: (_, record) => {
        const months = record.monthsLaying || 0;
        return (
          <Tooltip title={`Batch has been laying for ${months} months`}>
            <Tag
              color={months >= 18 ? "red" : months >= 16 ? "orange" : "green"}
            >
              {months} months
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Replacement Status",
      key: "replacementStatus",
      render: (_, record) => {
        const status = record.replacementStatus || "OK";
        return (
          <Tag
            color={getReplacementStatusColor(status)}
            icon={getReplacementStatusIcon(status)}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Production Change",
      key: "productionChange",
      render: (_, record) => {
        const change = record.previousProduction;
        if (!change) return <Tag color="default">N/A</Tag>;
        const isPositive = parseFloat(change) > 0;
        return (
          <Tag color={isPositive ? "success" : "error"}>
            {isPositive ? "+" : ""}
            {change}%
          </Tag>
        );
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Recorded By",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "Updated",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).fromNow(),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      defaultSortOrder: "descend",
    },
    // Only show actions in "current" mode
    ...(filters.urlToUse === "current"
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (_, record) => (
              <Space>
                <Tooltip title="Edit">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => openEditModal(record)}
                  />
                </Tooltip>
                <Popconfirm
                  title="Void this collection?"
                  description="This will move it to history and remove it from active records."
                  okText="Void"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                  onConfirm={() => handleVoid(record)}
                >
                  <Tooltip title="Void">
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ batch: null,farm: null, dateRange: null, urlToUse: "current" });
  };

  const csvLinkRef = useRef();

  const exportToCSVs = () => {
    const csvData = [
      [
        "ID",
        "Batch No.",
        "Normal Eggs",
        "Broken",
        "Double Yolk",
        "Middle Sized",
        "Spoilt",
        "Date",
        "Updated",
        "Notes",
      ],
      ...data.map((item) => [
        item.id,
        item.batch,
        item.normal_trays,
        item.broken_trays,
        item.double_yolk_trays,
        item.medium_trays,
        item.spoilt_trays,
        new Date(item.recorded).toLocaleDateString(),
        new Date(item.createdAt).toLocaleDateString(),
        item.remarks,
      ]),
    ];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob(["\ufeff", csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const csvUrl = URL.createObjectURL(blob);
    csvLinkRef.current.href = csvUrl;
    csvLinkRef.current.download = "sfarms.csv";
    csvLinkRef.current.click();
  };

  const EGGS_PER_TRAY = 30;
  const getTotalEggs = (trays = 0, eggs = 0) => trays * EGGS_PER_TRAY + eggs;
  const normalizeEggs = (trays = 0, eggs = 0) => {
    const totalEggs = getTotalEggs(trays, eggs);
    return {
      trays: Math.floor(totalEggs / EGGS_PER_TRAY),
      eggs: totalEggs % EGGS_PER_TRAY,
      totalEggs,
    };
  };
  const formatTrayLabel = (trays) =>
    `${trays} ${trays === 1 ? "tray" : "trays"}`;

  const categoryData = {};
  Object.keys(totals).forEach((key) => {
    const normalized = normalizeEggs(totals[key].trays, totals[key].eggs);
    categoryData[key] = { ...normalized };
  });

  const totalEggsAll = Object.values(categoryData).reduce(
    (sum, cat) => sum + cat.totalEggs,
    0,
  );
  Object.keys(categoryData).forEach((key) => {
    categoryData[key].percentage =
      totalEggsAll > 0
        ? ((categoryData[key].totalEggs / totalEggsAll) * 100).toFixed(1)
        : 0;
  });

  const totalCollectedRaw = Object.values(categoryData).reduce(
    (acc, cat) => {
      acc.trays += cat.trays;
      acc.eggs += cat.eggs;
      return acc;
    },
    { trays: 0, eggs: 0 },
  );

  const totalCollected = normalizeEggs(
    totalCollectedRaw.trays,
    totalCollectedRaw.eggs,
  );
  totalCollected.percentage = 100;

  const EggStatCard = ({ title, data, color }) => (
    <>
      <Statistic
        title={title}
        value={formatTrayLabel(data.trays)}
        valueStyle={{ color }}
      />
      <div style={{ fontSize: 14, color: "#888" }}>
        {data.eggs} {data.eggs === 1 ? "loose egg" : "loose eggs"}
      </div>
      <div style={{ fontSize: 14, marginTop: 4 }}>
        Total Eggs: <strong>{data.totalEggs}</strong>
      </div>
      <div style={{ fontSize: 14, color: "#888" }}>
        {data.percentage}% of collection
      </div>
    </>
  );

  const getReplacementStatusColor = (status) => {
    switch (status) {
      case "NEEDS REPLACEMENT":
        return "red";
      case "REPLACE SOON":
        return "orange";
      case "CONSIDER REPLACEMENT":
        return "gold";
      default:
        return "green";
    }
  };

  const getReplacementStatusIcon = (status) => {
    switch (status) {
      case "NEEDS REPLACEMENT":
        return <CloseCircleOutlined />;
      case "REPLACE SOON":
        return <WarningOutlined />;
      case "CONSIDER REPLACEMENT":
        return <InfoCircleOutlined />;
      default:
        return <CheckCircleOutlined />;
    }
  };

  return (
    <>
      <Card
        title="Egg Collections Dashboard"
        extra={
          <Space wrap>
            <Select
              placeholder="Filter by url"
              allowClear
              style={{ width: 200 }}
              value={filters.urlToUse}
              onChange={(val) => handleFilterChange("urlToUse", val)}
            >
              {urlOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Filter by Farm"
              allowClear
              style={{ width: 200 }}
              value={filters.farm}
              onChange={(val) => handleFilterChange("farm", val)}
            >
              {farmOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Filter by Batch"
              allowClear
              style={{ width: 200 }}
              value={filters.batch}
              onChange={(val) => handleFilterChange("batch", val)}
            >
              {batchOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange("dateRange", dates)}
              presets={[
                {
                  label: "Today",
                  value: [dayjs().startOf("day"), dayjs().endOf("day")],
                },
                {
                  label: "This Week",
                  value: [dayjs().startOf("week"), dayjs().endOf("week")],
                },
                {
                  label: "Last 14 Days",
                  value: [dayjs().subtract(14, "day"), dayjs()],
                },
                {
                  label: "This Month",
                  value: [dayjs().startOf("month"), dayjs().endOf("month")],
                },
                {
                  label: "This Year",
                  value: [dayjs().startOf("year"), dayjs().endOf("year")],
                },
              ]}
            />
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchCollections} />
            </Tooltip>
            <Button onClick={resetFilters}>Reset</Button>
            <Button onClick={exportToCSVs}>
              <span className="feather icon-file-text"></span> Export CSV
            </Button>
            <a ref={csvLinkRef} style={{ display: "none" }} />
            <Link to="/eggs/collection">
              <Button>
                <PlusOutlined /> Collect Eggs
              </Button>
            </Link>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Normal" data={categoryData.normal} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Broken" data={categoryData.broken} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Double Yolk" data={categoryData.doubleYolk} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Medium" data={categoryData.medium} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Spoilt" data={categoryData.spoilt} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <EggStatCard title="Total Collected" data={totalCollected} />
          </Col>
        </Row>

        <Divider />

        <Table
          width="100%"
          columns={columns}
          dataSource={data}
          size="small"
          rowKey="id"
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>

      {/* ---------------- EDIT MODAL ---------------- */}
      <Modal
        title={`Edit Egg Collection #${editingRecord?.id || ""}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingRecord(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        confirmLoading={savingEdit}
        okText="Save Changes"
        width={720}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleSaveEdit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="batch" label="Batch">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="recorded" label="Collection Date">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Egg Grades</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="normal_trays" label="Normal Trays">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="normal_eggs" label="Normal Loose Eggs">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="broken_trays" label="Broken Trays">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="broken_eggs" label="Broken Loose Eggs">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="double_yolk_trays" label="Double Yolk Trays">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="double_yolk_eggs" label="Double Yolk Loose Eggs">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="medium_trays" label="Medium Trays">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="medium_eggs" label="Medium Loose Eggs">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="spoilt_trays" label="Spoilt Trays">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="spoilt_eggs" label="Spoilt Loose Eggs">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={3} placeholder="Reason for edit or any notes" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EggCollectionsDashboard;
